/**
 * «Цена для хозяйства» — исключение на позиции ТМЦ (правка founder 15.08,
 * порция 4: «закупки вообще всегда на компанию, но надо дать опцию на
 * хозяйство»). Перекрывает цену компании ТОЛЬКО в расчётах этого хозяйства.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Stack } from '@/components/ui/stack';
import { matchesSearch } from '@/lib/reference/normalizeSearch';
import {
  formatPrice,
  parsePriceInput,
  priceUnitSuffix,
  useMeasurementUnits,
  type BudgetItem,
} from '@/lib/planning/budgetCatalog';
import { cn } from '@/lib/utils';

export interface FarmOption {
  id: string;
  name: string;
}

export function FarmPriceDialog({
  item,
  farms,
  initialFarmId,
  saving,
  onSubmit,
  onClose,
}: {
  item: BudgetItem;
  farms: FarmOption[];
  /** Задан → правим существующую цену хозяйства, выбор хозяйства заблокирован. */
  initialFarmId: string | null;
  saving: boolean;
  onSubmit: (values: { farm_id: string; price: number; price_source: string | null }) => void;
  onClose: () => void;
}) {
  const existing = item.farm_prices.find((p) => p.farm_id === initialFarmId) ?? null;
  const [farmId, setFarmId] = useState<string | null>(initialFarmId);
  const [term, setTerm] = useState('');
  const [price, setPrice] = useState(existing ? String(existing.price) : '');
  const [priceSource, setPriceSource] = useState(existing?.price_source ?? '');
  const units = useMeasurementUnits();
  const priceUnit = units.data?.find((u) => u.id === (item.price_unit_id ?? item.unit_id)) ?? null;

  const taken = new Set(item.farm_prices.map((p) => p.farm_id));
  const options = useMemo(
    () =>
      [...farms]
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
        .filter((f) => matchesSearch(f.name, term))
        .filter((f) => f.id === initialFarmId || !taken.has(f.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [farms, term, initialFarmId, item.farm_prices],
  );

  const parsed = parsePriceInput(price);
  // Пустое поле — ещё не ошибка: ругаемся только на мусор, а «Сохранить»
  // остаётся выключенным, пока цены нет.
  const invalid = parsed === undefined;
  const canSave = !!farmId && typeof parsed === 'number' && !saving;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Цена для хозяйства — {item.name}</DialogTitle>
          <DialogDescription>
            Цена компании — {formatPrice(item.planned_price)}. Цена хозяйства действует
            только в расчётах этого хозяйства, остальные считаются по цене компании.
          </DialogDescription>
        </DialogHeader>

        <Stack gap={3}>
          {!initialFarmId && (
            <div>
              <Label htmlFor="fp-search">Хозяйство</Label>
              <Input
                id="fp-search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Поиск хозяйства…"
              />
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border">
                {options.length === 0 && (
                  <p className="p-2 text-sm text-muted-foreground">
                    Свободных хозяйств нет — у всех уже своя цена.
                  </p>
                )}
                {options.map((farm) => (
                  <button
                    key={farm.id}
                    type="button"
                    aria-pressed={farm.id === farmId}
                    className={cn(
                      'block w-full px-3 py-1 text-left text-sm hover:bg-accent',
                      farm.id === farmId && 'bg-accent font-medium',
                    )}
                    onClick={() => setFarmId(farm.id)}
                  >
                    {farm.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="fp-price">Цена хозяйства, ₽{priceUnitSuffix(priceUnit)}</Label>
            <Input
              id="fp-price"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              aria-invalid={invalid || undefined}
              className="text-right tabular-nums"
            />
            {invalid && (
              <p className="mt-1 text-xs text-destructive">Цена — число не меньше нуля.</p>
            )}
          </div>

          <div>
            <Label htmlFor="fp-src">Почему своя цена</Label>
            <Input
              id="fp-src"
              value={priceSource}
              onChange={(e) => setPriceSource(e.target.value)}
              placeholder="свой договор, доставка, остатки прошлого года…"
            />
          </div>
        </Stack>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            disabled={!canSave}
            onClick={() =>
              onSubmit({
                farm_id: farmId as string,
                price: parsed as number,
                price_source: priceSource.trim() || null,
              })
            }
          >
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
